import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { GraduationCap, Users, BookOpen, Trophy, ArrowRight, Star, MapPin, Phone, Landmark, Shield, Train, ShieldCheck, FileText, ChevronRight, Headphones, Target, MonitorSmartphone, UserCheck, RefreshCw, Award, Sparkles, Zap, Clock, MessageCircle, ImageIcon, Camera } from "lucide-react";
import { courseCategories } from "@/data/courses";
import { testimonials, whyChooseUs } from "@/data/testimonials";
import { HeroCarousel } from "@/components/HeroCarousel";

const iconMap = {
  Landmark, Shield, Train, GraduationCap, ShieldCheck, FileText,
  HeadphonesIcon: Headphones, Target, MonitorSmartphone, UserCheck, RefreshCw, Award,
};

const stats = [
  { number: "500+", label: "Google Reviews", icon: Star, color: "from-vedanti-orange to-amber-400" },
  { number: "5.0", label: "Star Rating", icon: Trophy, color: "from-vedanti-mango to-yellow-400" },
  { number: "2", label: "Branches", icon: MapPin, color: "from-vedanti-navy to-blue-500" },
  { number: "10+", label: "Years Experience", icon: BookOpen, color: "from-green-500 to-emerald-400" },
];

const categoryColors = {
  "vedanti-navy": { bg: "bg-vedanti-navy/8", icon: "text-vedanti-navy", border: "border-vedanti-navy/20 hover:border-vedanti-navy/50", badge: "bg-vedanti-navy text-white" },
  "vedanti-orange": { bg: "bg-vedanti-orange/8", icon: "text-vedanti-orange", border: "border-vedanti-orange/20 hover:border-vedanti-orange/50", badge: "bg-vedanti-orange text-white" },
  "vedanti-mango": { bg: "bg-amber-50", icon: "text-amber-600", border: "border-amber-200 hover:border-amber-400", badge: "bg-vedanti-mango text-vedanti-navy" },
};

export default function HomePage() {
  return (
    <div data-testid="home-page">
      <Helmet>
        <title>Best Coaching in Badlapur for Banking & SSC | Vedanti</title>
        <meta name="description" content="Vedanti Academy is a top coaching institute in Badlapur for Banking (IBPS, SBI, RBI), SSC (CGL, CHSL), MBA CET, Railway, and Insurance exams. 500+ Google reviews, 5-star rated. Join now!" />
        <meta name="keywords" content="Vedanti Academy, banking coaching Badlapur, SSC coaching Badlapur, MBA CET coaching, IBPS coaching, SBI PO coaching, Railway exam preparation, insurance exam coaching, competitive exam classes near me" />
        <meta property="og:title" content="Vedanti Academy - Best Coaching for Banking, SSC, MBA & Railway Exams" />
        <meta property="og:description" content="Top-rated coaching institute in Badlapur. Expert faculty, AC classrooms, 360-degree doubt support. Proven results in Banking, SSC, MBA & Railway exams." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://vedantiacademy.com/" />
        <link rel="canonical" href="https://vedantiacademy.com/" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "EducationalOrganization",
          "name": "Vedanti Academy",
          "url": "https://vedantiacademy.com",
          "telephone": "+918308776505",
          "email": "vedantiacademy@gmail.com",
          "address": [
            {"@type": "PostalAddress", "streetAddress": "3rd Floor, Jadhav Market, above Aradhana Hotel", "addressLocality": "Badlapur", "addressRegion": "Maharashtra", "postalCode": "421503", "addressCountry": "IN"}
          ],
          "aggregateRating": {"@type": "AggregateRating", "ratingValue": "5.0", "reviewCount": "500", "bestRating": "5"},
          "description": "Top-rated coaching institute for Banking, SSC, MBA CET, Railway & Insurance exams in Badlapur, Maharashtra.",
          "areaServed": ["Badlapur","Dombivli","Ambernath","Ulhasnagar","Kalyan","Karjat","Neral","Vangni","Khopoli","Thane","Mumbai","Titwala","Asangaon","Shahapur","Kasara","Bhiwandi","Murbad","Panvel","Navi Mumbai","Airoli","Kopar Khairane","Vashi","Belapur","Kharghar","Pune"],
          "sameAs": ["https://www.instagram.com/vedantiacademy/","https://www.facebook.com/vedantiacademy/"]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "Vedanti Academy",
          "url": "https://vedantiacademy.com"
        })}</script>
      </Helmet>
      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        {/* Complex bg */}
        <div className="absolute inset-0 bg-vedanti-navy"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(249,115,22,0.15)_0%,_transparent_60%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_rgba(255,195,0,0.1)_0%,_transparent_60%)]"></div>
        <div className="absolute top-20 left-10 w-72 h-72 bg-vedanti-orange/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-20 w-96 h-96 bg-vedanti-mango/5 rounded-full blur-3xl"></div>

        <div className="container-main py-16 md:py-24 lg:py-28 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-vedanti-orange to-vedanti-mango text-white text-sm font-bold px-5 py-2 rounded-full mb-6 shadow-lg shadow-orange-500/20">
                <Sparkles size={16} />
                No.1 Coaching in Maharashtra for Competitive Exams
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.1] mb-6">
                Your Gateway to
                <span className="relative inline-block ml-3">
                  <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-vedanti-orange to-vedanti-mango">Government Jobs</span>
                  <span className="absolute bottom-1 left-0 w-full h-3 bg-vedanti-orange/20 rounded-full"></span>
                </span>
              </h1>
              <p className="text-lg text-slate-300 leading-relaxed mb-8 max-w-xl" data-testid="hero-full-name">
                <span className="font-semibold text-white">Vedanti Academy - Banking, MBA CET, CAT, CGL, IBPS, MPSC, CSAT, UPSC, Clerk, PO Coaching Classes</span> is the premier coaching for Banking, SSC CGL, CAT, MBA CET, Railways and other government job exams.
                <span className="text-vedanti-mango font-medium"> Shortcuts, Vedic Math</span>, and personalized mentorship to crack any competitive exam.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/demo-class"
                  className="bg-gradient-to-r from-vedanti-orange to-amber-500 text-white hover:from-vedanti-orange hover:to-vedanti-orange rounded-xl px-8 py-4 font-bold text-lg shadow-lg shadow-orange-500/30 hover:-translate-y-1 hover:shadow-xl transition-all inline-flex items-center gap-2"
                  data-testid="hero-demo-class-btn"
                >
                  Book Free Demo Class <ArrowRight size={20} />
                </Link>
                <Link
                  to="/courses"
                  className="border-2 border-white/20 text-white hover:bg-white hover:text-vedanti-navy rounded-xl px-8 py-4 font-semibold transition-all inline-flex items-center gap-2 backdrop-blur-sm"
                  data-testid="hero-explore-courses-btn"
                >
                  Explore Courses
                </Link>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a href="tel:+918308776505" className="text-white font-semibold flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full transition-colors" data-testid="hero-phone">
                  <Phone size={18} className="text-vedanti-orange" /> +91 8308776505
                </a>
                <a href="https://wa.me/918308776505?text=Hi%2C%20I%27m%20interested%20in%20coaching%20at%20Vedanti%20Academy." target="_blank" rel="noopener noreferrer nofollow" className="text-white font-semibold flex items-center gap-2 bg-green-500/20 hover:bg-green-500/30 px-4 py-2 rounded-full transition-colors" data-testid="hero-whatsapp">
                  <MessageCircle size={18} className="text-green-400" /> WhatsApp Us
                </a>
              </div>
            </div>
            <div className="hidden lg:block animate-fade-in-up animate-delay-200">
              <div className="relative">
                <div className="absolute -inset-3 bg-gradient-to-br from-vedanti-orange/30 to-vedanti-mango/20 rounded-3xl rotate-2 blur-sm"></div>
                <div className="absolute -inset-3 bg-gradient-to-br from-vedanti-orange/20 to-vedanti-mango/10 rounded-3xl -rotate-1"></div>
                <div className="relative">
                  <HeroCarousel />
                </div>
                {/* Floating badges */}
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-2xl border border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-vedanti-orange to-amber-400 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/20">
                      <Users size={22} className="text-white" />
                    </div>
                    <div>
                      <p className="font-heading font-extrabold text-vedanti-navy text-xl leading-tight">500+</p>
                      <div className="flex gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <Star key={`hero-star-${i}`} size={12} className="fill-vedanti-mango text-vedanti-mango" />
                        ))}
                      </div>
                      <p className="text-[10px] text-slate-400 mt-0.5">Google Reviews</p>
                    </div>
                  </div>
                </div>
                <div className="absolute -top-4 -right-4 bg-white rounded-2xl p-3 shadow-2xl border border-slate-100">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-400 rounded-lg flex items-center justify-center">
                      <Zap size={18} className="text-white" />
                    </div>
                    <div>
                      <p className="font-heading font-bold text-vedanti-navy text-sm">30+ Courses</p>
                      <p className="text-[10px] text-slate-400">All Exams Covered</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 60L48 55C96 50 192 40 288 35C384 30 480 30 576 33.3C672 36.7 768 43.3 864 45C960 46.7 1056 43.3 1152 38.3C1248 33.3 1344 26.7 1392 23.3L1440 20V60H1392C1344 60 1248 60 1152 60C1056 60 960 60 864 60C768 60 672 60 576 60C480 60 384 60 288 60C192 60 96 60 48 60H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-white relative -mt-1" data-testid="stats-section">
        <div className="container-main py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {stats.map((stat) => {
              const Icon = stat.icon;
              return (
                <div key={stat.label} className="text-center p-5 rounded-2xl bg-slate-50 border border-slate-100 hover:shadow-md transition-all card-hover">
                  <div className={`w-14 h-14 mx-auto mb-3 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center shadow-lg`}>
                    <Icon size={24} className="text-white" />
                  </div>
                  <p className="font-heading font-extrabold text-3xl md:text-4xl text-vedanti-navy">{stat.number}</p>
                  <p className="text-sm text-slate-500 font-medium mt-1">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Course Categories */}
      <section className="section-padding relative overflow-hidden" data-testid="course-categories-section">
        <div className="absolute inset-0 bg-gradient-to-b from-white via-orange-50/30 to-white"></div>
        <div className="container-main relative z-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-vedanti-orange/10 text-vedanti-orange font-bold text-sm uppercase tracking-wider px-4 py-1.5 rounded-full">Our Programs</span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-vedanti-navy mt-3">
              Explore Our <span className="text-vedanti-orange">Courses</span>
            </h2>
            <p className="text-slate-500 mt-3 max-w-2xl mx-auto text-base md:text-lg">Comprehensive coaching for all major competitive exams with expert faculty and proven results</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courseCategories.map((cat, idx) => {
              const Icon = iconMap[cat.icon] || BookOpen;
              const colors = categoryColors[cat.color] || categoryColors["vedanti-navy"];
              return (
                <Link
                  key={cat.id}
                  to={`/courses?category=${cat.id}`}
                  className={`bg-white rounded-2xl border-2 ${colors.border} shadow-sm hover:shadow-xl transition-all p-7 group card-hover relative overflow-hidden`}
                  data-testid={`course-category-${cat.id}`}
                >
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-slate-50 to-transparent rounded-bl-full opacity-50"></div>
                  <div className="relative z-10">
                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-5 ${colors.bg} group-hover:scale-110 transition-transform`}>
                      <Icon size={30} className={colors.icon} />
                    </div>
                    <h3 className="font-heading font-bold text-xl text-vedanti-navy mb-2">{cat.name}</h3>
                    <p className="text-slate-500 text-sm leading-relaxed mb-5">{cat.description}</p>
                    <span className="inline-flex items-center gap-1.5 text-vedanti-orange font-semibold text-sm group-hover:gap-3 transition-all">
                      View Courses <ChevronRight size={16} />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
          <div className="text-center mt-10">
            <Link to="/courses" className="inline-flex items-center gap-2 bg-vedanti-navy text-white hover:bg-vedanti-navy/90 rounded-xl px-8 py-3.5 font-semibold transition-all shadow-lg hover:-translate-y-0.5" data-testid="view-all-courses-btn">
              View All 30+ Courses <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-vedanti-navy relative overflow-hidden" data-testid="why-choose-us-section">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(249,115,22,0.08)_0%,_transparent_70%)]"></div>
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-vedanti-orange via-vedanti-mango to-vedanti-orange"></div>
        <div className="container-main relative z-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-vedanti-orange/20 text-vedanti-orange font-bold text-sm uppercase tracking-wider px-4 py-1.5 rounded-full">Why Vedanti Academy</span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-white mt-3">
              Why We <span className="text-vedanti-mango">Stand Out</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChooseUs.map((item, idx) => {
              const Icon = iconMap[item.icon] || Award;
              const gradients = [
                "from-vedanti-orange to-amber-400",
                "from-vedanti-mango to-yellow-400",
                "from-blue-500 to-cyan-400",
                "from-green-500 to-emerald-400",
                "from-purple-500 to-pink-400",
                "from-vedanti-orange to-red-400",
              ];
              return (
                <div key={item.title} className="p-6 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-vedanti-orange/30 transition-all card-hover backdrop-blur-sm">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${gradients[idx]} flex items-center justify-center mb-4 shadow-lg`}>
                    <Icon size={24} className="text-white" />
                  </div>
                  <h3 className="font-heading font-bold text-lg text-white mb-2">{item.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding relative overflow-hidden" data-testid="testimonials-section">
        <div className="absolute inset-0 bg-gradient-to-b from-white via-amber-50/30 to-white"></div>
        <div className="container-main relative z-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-vedanti-mango/15 text-amber-700 font-bold text-sm uppercase tracking-wider px-4 py-1.5 rounded-full">Student Voices</span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-vedanti-navy mt-3">
              What Our <span className="text-vedanti-orange">Students Say</span>
            </h2>
            <div className="flex items-center justify-center gap-2 mt-3">
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={`review-star-${i}`} size={18} className="fill-vedanti-mango text-vedanti-mango" />
                ))}
              </div>
              <span className="text-slate-500 font-medium">Based on 500+ Google Reviews</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.slice(0, 6).map((t, idx) => {
              const borderColors = ["border-vedanti-orange/30", "border-vedanti-mango/30", "border-blue-200", "border-green-200", "border-vedanti-orange/30", "border-vedanti-mango/30"];
              const topColors = ["bg-vedanti-orange", "bg-vedanti-mango", "bg-blue-500", "bg-green-500", "bg-vedanti-orange", "bg-vedanti-mango"];
              return (
                <div key={t.name} className={`bg-white rounded-2xl p-6 border-2 ${borderColors[idx]} shadow-sm hover:shadow-lg transition-all card-hover relative overflow-hidden`} data-testid={`testimonial-${idx}`}>
                  <div className={`absolute top-0 left-0 right-0 h-1 ${topColors[idx]}`}></div>
                  <div className="flex gap-0.5 mb-4 mt-2">
                    {[...Array(t.rating)].map((_, i) => (
                      <Star key={`star-${i}`} size={16} className="fill-vedanti-mango text-vedanti-mango" />
                    ))}
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed mb-5">&ldquo;{t.review}&rdquo;</p>
                  <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                    <div className={`w-10 h-10 rounded-full ${topColors[idx]} flex items-center justify-center text-white font-bold text-sm`}>
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-heading font-bold text-vedanti-navy text-sm">{t.name}</p>
                      <p className="text-xs text-slate-400">{t.course}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Achievers Gallery CTA */}
      <section className="relative overflow-hidden" data-testid="gallery-cta-section">
        <div className="absolute inset-0 bg-vedanti-navy"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(249,115,22,0.15)_0%,_transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_rgba(251,191,36,0.1)_0%,_transparent_50%)]"></div>
        <div className="container-main py-16 md:py-20 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-vedanti-orange/15 text-vedanti-orange font-bold text-sm uppercase tracking-wider px-4 py-1.5 rounded-full mb-5">
              <Camera size={16} /> Wall of Fame
            </div>
            <h2 className="font-heading text-3xl md:text-5xl font-extrabold text-white mb-5">
              Meet Our <span className="text-vedanti-mango">Achievers</span>
            </h2>
            <p className="text-slate-300 text-lg leading-relaxed mb-7">
              Our students have cracked IBPS, SBI, RBI, SSC CGL, Railway NTPC, MBA CET, LIC and many more competitive exams. Real selections, real success stories.
            </p>
            <div className="flex flex-wrap gap-2 justify-center mb-8">
              {["Banking", "SSC", "Railway", "MBA CET", "Insurance", "Govt Exams"].map((tag) => (
                <span key={tag} className="px-3.5 py-1.5 text-xs font-semibold rounded-full bg-white/10 text-slate-200 border border-white/10">
                  {tag}
                </span>
              ))}
            </div>
            <Link
              to="/gallery"
              className="inline-flex items-center gap-2 bg-vedanti-orange hover:bg-orange-600 text-white rounded-xl px-8 py-4 font-bold text-lg shadow-xl hover:shadow-vedanti-orange/25 hover:-translate-y-0.5 transition-all"
              data-testid="gallery-cta-btn"
            >
              <ImageIcon size={22} /> View Achievers Gallery <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Locations Strip */}
      <section className="py-12 bg-slate-50 border-y border-slate-100" data-testid="locations-section">
        <div className="container-main">
          <div className="text-center mb-6">
            <h3 className="font-heading text-xl font-bold text-vedanti-navy">Serving Students From</h3>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {["Badlapur", "Ambernath", "Ulhasnagar", "Kalyan", "Dombivali", "Karjat", "Neral", "Vangni", "Khopoli", "Katrap", "Panvel Highway Area", "Shirgaon", "Aptewadi", "Gandhi Chowk", "Shivaji Chowk", "Surwal Chowk", "Gaondevi", "Mohan Palm", "Ambedkar Chowk", "Manjarli", "Pawar Complex", "Rameshwadi", "Badlapur Gaon", "Belavali", "Ganesh Chowk"].map((loc) => (
              <span key={loc} className="px-5 py-2.5 rounded-full bg-white border border-slate-200 text-slate-700 font-medium text-sm hover:border-vedanti-orange hover:text-vedanti-orange hover:shadow-sm transition-all cursor-default">
                <MapPin size={14} className="inline mr-1.5 -mt-0.5" />{loc}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-gradient-to-r from-vedanti-orange via-amber-500 to-vedanti-mango"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(255,255,255,0.1)_0%,_transparent_70%)]"></div>
        <div className="container-main py-16 md:py-20 relative z-10 text-center">
          <h2 className="font-heading text-3xl md:text-5xl font-extrabold text-white mb-4">Ready to Start Your Journey?</h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">Join Vedanti Academy today and take the first step towards your dream government job. Book a free consultancy session now.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/consultancy"
              className="bg-white text-vedanti-navy hover:bg-slate-100 rounded-xl px-8 py-4 font-bold text-lg shadow-xl hover:-translate-y-1 transition-all"
              data-testid="cta-consultancy-btn"
            >
              Book Free Consultancy
            </Link>
            <a
              href="tel:+918308776505"
              className="border-2 border-white text-white hover:bg-white hover:text-vedanti-orange rounded-xl px-8 py-4 font-semibold transition-all inline-flex items-center gap-2"
              data-testid="cta-call-btn"
            >
              <Phone size={20} /> Call Now
            </a>
            <a
              href="https://wa.me/918308776505?text=Hi%2C%20I%27m%20interested%20in%20coaching%20at%20Vedanti%20Academy."
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="border-2 border-white text-white hover:bg-white hover:text-green-600 rounded-xl px-8 py-4 font-semibold transition-all inline-flex items-center gap-2"
              data-testid="cta-whatsapp-btn"
            >
              <MessageCircle size={20} /> WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
